import { Link } from "react-router-dom";
import { Warehouse, Shield, Clock, TrendingUp } from "lucide-react";

const HomePage = () => {
  const features = [
    {
      icon: Warehouse,
      title: "Flexible Storage",
      description: "Scalable solutions that adapt to your seasonal peaks and growth phases.",
    },
    {
      icon: Shield,
      title: "Secure Facility",
      description: "Your goods are protected with 24/7 security and monitored access.",
    },
    {
      icon: Clock,
      title: "Reliable Access",
      description: "Storage exactly when your business needs it most.",
    },
    {
      icon: TrendingUp,
      title: "Cost Effective",
      description: "Competitive rates ensure you only pay for the space you need.",
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-white py-24 md:py-32" data-testid="hero-section">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_palletspace-preview/artifacts/co90wzu7_pallet1.jpeg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold tracking-wider uppercase text-orange-500 mb-4 animate-fade-in">
              Pallet Storage Solutions
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6 animate-slide-up">
              Reliable Pallet Storage for UK Business
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 animate-slide-up animation-delay-100">
              External pallet storage is a smart, cost-effective way to cut overheads and unlock extra capacity. At PalletSpace.uk, we provide flexible and scalable pallet storage solutions designed to adapt to your needs. Whether you're managing seasonal peaks or navigating growth phases, our competitive pallet storage rates ensure you only pay for the space you need. With our support, you'll always have reliable, secure pallet storage exactly when your business needs it most.
            </p>
            <div className="flex flex-wrap gap-4 animate-slide-up animation-delay-200">
              <Link
                to="/contact"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 transition-all duration-200 shadow-sm hover:shadow-md"
                data-testid="hero-get-quote"
              >
                Get a Quote
              </Link>
              <Link
                to="/prices"
                className="bg-white border border-slate-200 text-slate-900 hover:border-orange-500 hover:text-orange-500 font-bold py-4 px-8 transition-colors"
                data-testid="hero-view-prices"
              >
                View Prices
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-slate-50 py-24 md:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-wider uppercase text-orange-500 mb-4">
              Why Choose Us
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Storage You Can Rely On
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white border border-slate-100 p-8 hover:border-orange-500/30 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                data-testid={`feature-card-${index}`}
              >
                <feature.icon className="w-10 h-10 text-orange-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Divider */}
      <section className="relative h-64 md:h-96" data-testid="image-divider">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_palletspace-preview/artifacts/co90wzu7_pallet1.jpeg)`,
          }}
        />
        <div className="absolute inset-0 bg-slate-900/50" />
        {/* Forklift overlay */}
        <div 
          className="absolute bottom-0 right-0 w-1/3 h-full opacity-90 hidden md:block"
          style={{
            backgroundImage: `url(https://images.pexels.com/photos/8760709/pexels-photo-8760709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            maskImage: 'linear-gradient(to left, black 60%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to left, black 60%, transparent 100%)',
          }}
        />
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center text-white px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
              Professional Logistics Support
            </h2>
            <p className="text-slate-200 text-base md:text-lg">
              From storage to fulfilment, we've got you covered
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24 md:py-32" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4">
            Ready to Free Up Your Space?
          </h2>
          <p className="text-slate-600 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            Get in touch today for a competitive quote tailored to your business needs.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 transition-all duration-200 shadow-sm hover:shadow-md"
            data-testid="cta-contact"
          >
            Contact Us Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
